import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const ORANGE = '#FF4D00';
const BLACK = '#000000';
const WHITE = '#FFFFFF';
const GRAY_100 = '#F5F5F5';
const GRAY_400 = '#999999';
const GRAY_600 = '#666666';
const GRAY_800 = '#222222';
const GRAY_900 = '#111111';
const GREEN = '#2A9D5C';

const RADIUS_MILES = 3;
const ELMHURST_CENTER = { lat: 41.8968, lng: -87.9503 };
const TRAIN_CROSSINGS = [
  { id: 'cross_001', name: 'West Ave', cross: 'UP/Metra Tracks', lat: 41.89845, lng: -87.95929, carrier: 'UP', subdivision: 'Geneva Sub', priority: 'high', typical_duration: '5-20 min' },
  { id: 'cross_002', name: 'York St', cross: 'UP/Metra Tracks', lat: 41.89954, lng: -87.93997, carrier: 'UP', subdivision: 'Geneva Sub', priority: 'high', typical_duration: '5-15 min' },
  { id: 'cross_003', name: 'St. Charles Rd', cross: 'UP/Metra Tracks', lat: 41.89031, lng: -87.95336, carrier: 'UP', subdivision: 'Geneva Sub', priority: 'high', typical_duration: '5-20 min' },
  { id: 'cross_004', name: 'Spring Rd', cross: 'UP/Metra Tracks', lat: 41.88794, lng: -87.94969, carrier: 'UP', subdivision: 'Geneva Sub', priority: 'high', typical_duration: '5-15 min' },
  { id: 'cross_005', name: 'York St (South)', cross: 'IHB Tracks', lat: 41.88190, lng: -87.93936, carrier: 'IHB', subdivision: 'Indiana Harbor Belt', priority: 'medium', typical_duration: '5-15 min' },
  { id: 'cross_006', name: 'Poplar Ave', cross: 'UP/Metra Tracks', lat: 41.89850, lng: -87.93015, carrier: 'UP', subdivision: 'Geneva Sub', priority: 'medium', typical_duration: '5-10 min' },
];

const WTFApp = () => {
  const [userLocation, setUserLocation] = useState(null);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [nearbyReports, setNearbyReports] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportText, setReportText] = useState('');
  const [selectedCrossing, setSelectedCrossing] = useState('');
  const [activeView, setActiveView] = useState('crossings');
  const [userVoted, setUserVoted] = useState({});
  const [showMenu, setShowMenu] = useState(false);
  const [schedule, setSchedule] = useState({ metra: [], freight: [], dayType: 'weekday' });
  const [crossingStatuses, setCrossingStatuses] = useState({});
  const [showPatterns, setShowPatterns] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showWelcome, setShowWelcome] = useState(() => {
    try { return !localStorage.getItem('wtf_welcomed'); } catch { return true; }
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Device detection
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = /Android/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
  const showInstallOption = (isIOS || isAndroid || deferredPrompt) && !isStandalone;

  useEffect(() => {
    // Capture Android/Chrome install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.watchPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => setUserLocation(ELMHURST_CENTER),
        { enableHighAccuracy: true }
      );
    } else {
      setUserLocation(ELMHURST_CENTER);
    }
  }, []);

  useEffect(() => {
    const fetchTrains = async () => {
      try {
        const lat = userLocation?.lat || ELMHURST_CENTER.lat;
        const lng = userLocation?.lng || ELMHURST_CENTER.lng;
        const response = await fetch(`${API_BASE}/api/active-trains?lat=${lat}&lng=${lng}`);
        const data = await response.json();
        if (data.success && data.trains) {
          const alerts = data.trains.map((train) => ({
            id: train.id,
            crossing: train.nextCrossing || train.crossingName || 'Unknown Crossing',
            lat: train.latitude || train.lat,
            lng: train.longitude || train.lng,
            source: train.source === 'scraped' ? 'UP/Metra Feed' : 'Community Report',
            timestamp: new Date(train.reportedAt?._seconds * 1000 || train.lastUpdated || Date.now()),
            eta: train.eta_minutes || null,
            confirmations: train.votes || 1,
          }));
          setActiveAlerts(alerts.slice(0, 5));
        }
      } catch (err) {
        console.log('Backend not available, using demo mode');
        if (activeAlerts.length === 0) {
          loadDemoData();
        }
      }
    };

    fetchTrains();
    const interval = setInterval(fetchTrains, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation]);

  // Fetch Metra schedule + freight windows
  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/schedule`);
        const data = await res.json();
        if (data.success) {
          setSchedule({ metra: data.metra || [], freight: data.freight || [], dayType: data.dayType });
        }
      } catch (err) {
        // Fallback: no schedule data
      }
    };
    fetchSchedule();
    const interval = setInterval(fetchSchedule, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  // Fetch crossing statuses + Amtrak
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const statusRes = await fetch(`${API_BASE}/api/crossing-status-all`).catch(() => null);
        if (statusRes) {
          const data = await statusRes.json();
          if (data.success) setCrossingStatuses(data.statuses || {});
        }
      } catch (err) {
        // Fallback: no status data
      }
    };
    fetchStatuses();
    const interval = setInterval(fetchStatuses, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const updateCrossingStatus = async (crossingId, status) => {
    // Optimistic update — properly recalculate current_status from vote counts
    setCrossingStatuses((prev) => {
      const existing = prev[crossingId] || {};
      const existingConfirmations = existing.confirmations || { train_present: 0, clear: 0 };

      // When someone reports "clear" and status was "train_present", halve the train votes
      // (matches backend logic — a fresh "clear" report overrides stale "train" votes)
      let trainVotes = existingConfirmations.train_present || 0;
      let clearVotes = existingConfirmations.clear || 0;

      if (status === 'clear' && existing.current_status === 'train_present') {
        trainVotes = Math.floor(trainVotes / 2);
      }

      // Increment the vote for the reported status
      if (status === 'train_present') trainVotes += 1;
      if (status === 'clear') clearVotes += 1;

      // Determine dominant status (same logic as backend)
      let newStatus;
      if (clearVotes > trainVotes) newStatus = 'clear';
      else if (trainVotes > clearVotes) newStatus = 'train_present';
      else newStatus = 'unknown';

      return {
        ...prev,
        [crossingId]: {
          ...existing,
          current_status: newStatus,
          confirmations: { train_present: trainVotes, clear: clearVotes },
          traffic_source: false, // Human report overrides traffic probe
        },
      };
    });

    try {
      const loc = userLocation || ELMHURST_CENTER;
      await fetch(`${API_BASE}/api/crossing-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          crossing_id: crossingId,
          status,
          latitude: loc.lat,
          longitude: loc.lng,
        }),
      });
    } catch (err) {
      console.log('Status update saved locally');
    }
  };

  const refreshAll = async () => {
    setIsRefreshing(true);
    try {
      const lat = userLocation?.lat || ELMHURST_CENTER.lat;
      const lng = userLocation?.lng || ELMHURST_CENTER.lng;
      const [trainsRes, scheduleRes, statusRes] = await Promise.all([
        fetch(`${API_BASE}/api/active-trains?lat=${lat}&lng=${lng}`).catch(() => null),
        fetch(`${API_BASE}/api/schedule`).catch(() => null),
        fetch(`${API_BASE}/api/crossing-status-all`).catch(() => null),
      ]);
      if (trainsRes) {
        const data = await trainsRes.json();
        if (data.success && data.trains) {
          const alerts = data.trains.map((train) => ({
            id: train.id,
            crossing: train.nextCrossing || train.crossingName || 'Unknown Crossing',
            lat: train.latitude || train.lat,
            lng: train.longitude || train.lng,
            source: train.source === 'scraped' ? 'UP/Metra Feed' : 'Community Report',
            timestamp: new Date(train.reportedAt?._seconds * 1000 || train.lastUpdated || Date.now()),
            eta: train.eta_minutes || null,
            confirmations: train.votes || 1,
          }));
          setActiveAlerts(alerts.slice(0, 5));
        }
      }
      if (scheduleRes) {
        const data = await scheduleRes.json();
        if (data.success) setSchedule({ metra: data.metra || [], freight: data.freight || [], dayType: data.dayType });
      }
      if (statusRes) {
        const data = await statusRes.json();
        if (data.success) setCrossingStatuses(data.statuses || {});
      }
    } catch (err) {
      console.log('Refresh failed:', err);
    }
    setTimeout(() => setIsRefreshing(false), 400); // brief animation
  };

  const loadDemoData = () => {
    const mockReports = [
      {
        id: 'r1',
        crossing: 'York St',
        lat: 41.89954,
        lng: -87.93997,
        reportedBy: 'Sarah M.',
        text: 'Freight train just started crossing, looks long — maybe 10 mins',
        timestamp: new Date(Date.now() - 2 * 60000),
        votes: 12,
      },
      {
        id: 'r2',
        crossing: 'Spring Rd',
        lat: 41.88794,
        lng: -87.94969,
        reportedBy: 'Mike D.',
        text: 'Heard horn, coming from west — ETA ~8 mins',
        timestamp: new Date(Date.now() - 5 * 60000),
        votes: 8,
      },
      {
        id: 'r3',
        crossing: 'St. Charles Rd',
        lat: 41.89031,
        lng: -87.95336,
        reportedBy: 'Jessica L.',
        text: 'Stopped for 15 mins. Now starting to move.',
        timestamp: new Date(Date.now() - 12 * 60000),
        votes: 5,
      },
    ];
    setNearbyReports(mockReports);
    setActiveAlerts([
      {
        id: 'demo1',
        crossing: 'York St',
        lat: 41.89954,
        lng: -87.93997,
        source: 'Community Report',
        timestamp: new Date(),
        eta: 8,
        confirmations: 4,
      },
    ]);
  };

  const handleReportTrain = async () => {
    if (!reportText.trim()) return;

    const loc = userLocation || ELMHURST_CENTER;
    const newReport = {
      id: `r${Date.now()}`,
      crossing: selectedCrossing || 'Your Location',
      lat: loc.lat,
      lng: loc.lng,
      reportedBy: 'You',
      text: reportText,
      timestamp: new Date(),
      votes: 1,
    };

    setNearbyReports((prev) => [newReport, ...prev]);
    setReportText('');
    setSelectedCrossing('');
    setShowReportModal(false);

    try {
      await fetch(`${API_BASE}/api/report-train`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          latitude: loc.lat,
          longitude: loc.lng,
          description: reportText,
          crossingName: selectedCrossing || 'User Location',
        }),
      });
    } catch (err) {
      console.log('Report saved locally (backend offline)');
    }

    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Report Submitted', {
        body: 'Your train sighting helps the community.',
      });
    }
  };

  const handleVote = async (reportId) => {
    setUserVoted((prev) => ({ ...prev, [reportId]: !prev[reportId] }));
    setNearbyReports((prev) =>
      prev.map((r) =>
        r.id === reportId
          ? { ...r, votes: r.votes + (userVoted[reportId] ? -1 : 1) }
          : r
      )
    );

    try {
      await fetch(`${API_BASE}/api/vote-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId }),
      });
    } catch (err) {}
  };

  const [notifStatus, setNotifStatus] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );

  const requestNotifications = async () => {
    if (!('Notification' in window)) {
      alert('Notifications are not supported on this browser. Add to Home Screen on iOS for the best experience.');
      return;
    }
    if (Notification.permission === 'granted') {
      new Notification('WTF? Alerts Active', {
        body: 'You will be notified when trains block crossings.',
      });
      return;
    }
    if (Notification.permission === 'denied') {
      alert('Notifications are blocked. Go to your browser settings to enable them for this site.');
      return;
    }
    const result = await Notification.requestPermission();
    setNotifStatus(result);
    if (result === 'granted') {
      new Notification('WTF? Alerts Enabled', {
        body: 'You will be notified when trains block crossings near you.',
      });
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  const dateStr = now.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

  // Combine API alerts + crowd-reported blocked crossings for hero banner
  const crowdBlockedCrossings = TRAIN_CROSSINGS.filter(
    (c) => crossingStatuses[c.id]?.current_status === 'train_present'
  );
  const totalBlockedCount = activeAlerts.length + crowdBlockedCrossings.filter(
    (c) => !activeAlerts.some((a) => a.crossing === c.name)
  ).length;
  const hasActiveBlockage = totalBlockedCount > 0;

  return (
    <div style={styles.app}>
      {/* ─── HEADER ─── */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.headerLeft}>
            <div style={styles.logoMark}>
              <i className="ri-arrow-right-up-line" style={styles.logoArrow}></i>
            </div>
            <div>
              <h1 style={styles.logoText}>WTF?</h1>
              <p style={styles.logoSub}>Where's The Freight?</p>
            </div>
          </div>
          <div style={styles.headerRight}>
            <span style={styles.headerLocation}>Elmhurst, IL</span>
            <button
              onClick={refreshAll}
              disabled={isRefreshing}
              style={styles.refreshBtn}
              aria-label="Refresh"
            >
              <i className="ri-refresh-line" style={{
                fontSize: 16,
                display: 'block',
                animation: isRefreshing ? 'spin 0.6s linear infinite' : 'none',
              }}></i>
            </button>
            <button onClick={() => setShowMenu(!showMenu)} style={styles.menuBtn}>
              {showMenu ? 'Close' : 'Menu'}
            </button>
          </div>
        </div>
        {showMenu && (
          <div style={styles.menuPanel}>
            <div style={styles.menuInner}>
              <button onClick={() => { requestNotifications(); setShowMenu(false); }} style={styles.menuItem}>
                <i className={notifStatus === 'granted' ? 'ri-notification-3-line' : 'ri-notification-off-line'} style={{ fontSize: 16 }}></i>
                <span>{notifStatus === 'granted' ? 'Alerts On' : 'Enable Alerts'}</span>
                {notifStatus === 'granted' && <span style={styles.menuBadge}>Active</span>}
              </button>
              <div style={styles.menuDivider} />
              <a href="https://venmo.com/joseph-peart" target="_blank" rel="noopener noreferrer" style={styles.menuItem}>
                <i className="ri-cup-line" style={{ fontSize: 16 }}></i>
                <span>Buy Joseph a Coffee</span>
              </a>
              <div style={styles.menuDivider} />
              <a href="mailto:joseph@letsgobolder.com?subject=WTF%3F%20App%20Feedback" style={styles.menuItem}>
                <i className="ri-feedback-line" style={{ fontSize: 16 }}></i>
                <span>Send Feedback</span>
              </a>
              {showInstallOption && (
                <>
                  <div style={styles.menuDivider} />
                  {isIOS ? (
                    <div style={styles.installCard}>
                      <div style={styles.installHeader}>
                        <i className="ri-smartphone-line" style={{ fontSize: 18, color: ORANGE }}></i>
                        <span style={styles.installTitle}>Add to Home Screen</span>
                      </div>
                      <p style={styles.installText}>
                        Get the full app experience — tap{' '}
                        <i className="ri-share-line" style={{ fontSize: 13, verticalAlign: 'middle' }}></i>{' '}
                        in Safari, then scroll down and tap <strong>"Add to Home Screen"</strong>.
                      </p>
                    </div>
                  ) : (
                    <button onClick={handleInstall} style={styles.menuItem}>
                      <i className="ri-install-line" style={{ fontSize: 16 }}></i>
                      <span>Install App</span>
                    </button>
                  )}
                </>
              )}
              <div style={styles.menuDivider} />
              <p style={styles.menuAbout}>
                WTF? is a free app built by a dad who just wants to get his kid
                to their activity on time without getting stuck at a train crossing.
                If it helps you navigate the streets of Elmhurst too, that's a win.
              </p>
              <p style={styles.menuLegal}>
                WTF? is a community-powered tool. Metra times are based on published
                schedules and do not reflect real-time delays or cancellations.
                Freight train estimates are approximate — freight railroads do not
                publish schedules. Crossing status relies on community reports and
                traffic data, which may be inaccurate. Do not rely on this app for
                safety-critical decisions. Not affiliated with Union Pacific, Metra,
                or the Village of Elmhurst. Use at your own risk.
              </p>
              <p style={styles.menuLegal}>
                &copy; {new Date().getFullYear()} The Bolder Group. All rights reserved.
              </p>
            </div>
          </div>
        )}
      </header>

      {/* ─── HERO STATUS ─── */}
      <section style={{
        ...styles.hero,
        background: hasActiveBlockage ? ORANGE : GREEN,
        borderBottom: 'none',
      }}>
        <div style={styles.heroInner}>
          <div style={styles.heroTop}>
            <p style={{
              ...styles.heroLabel,
              color: hasActiveBlockage ? BLACK : 'rgba(0,0,0,0.7)',
            }}>Live Status</p>
            <p style={{
              ...styles.heroTime,
              color: hasActiveBlockage ? BLACK : 'rgba(0,0,0,0.7)',
            }}>{timeStr}</p>
          </div>
          <h2 style={{
            ...styles.heroTitle,
            color: BLACK,
          }}>
            {hasActiveBlockage ? (
              <>
                {totalBlockedCount} Active
                <br />
                Crossing{totalBlockedCount !== 1 ? 's' : ''}
              </>
            ) : (
              <>
                All Clear
              </>
            )}
          </h2>
          <div style={{
            ...styles.heroDivider,
            background: 'rgba(0,0,0,0.2)',
          }} />
          <div style={{
            ...styles.heroMeta,
            color: 'rgba(0,0,0,0.7)',
          }}>
            <span>{dateStr}</span>
            <span>{TRAIN_CROSSINGS.length} crossings monitored</span>
            <span>{RADIUS_MILES} mi radius</span>
          </div>
          <p style={{
            fontSize: 9,
            fontWeight: 500,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: 'rgba(0,0,0,0.4)',
            marginTop: 8,
            textAlign: 'center',
          }}>
            Based on schedules, estimates & community reports — not real-time tracking
          </p>
        </div>
      </section>

      {/* ─── TRAIN PATTERNS BAR ─── */}
      {(() => {
        // Build hourly density from Metra schedule + freight windows
        const hours = Array.from({ length: 24 }, (_, i) => ({
          hour: i,
          label: i === 0 ? '12a' : i < 12 ? `${i}a` : i === 12 ? '12p' : `${i - 12}p`,
          metra: 0,
          freight: 0,
        }));

        // Count Metra trains per hour from static schedule data
        const allScheduleTimes = [
          // Inbound (weekday estimates)
          5,5,6,6,6,6,7,7,7,7,7,8,8,8,9,10,11,13,14,15,16,17,18,18,19,20,21,22,23,
          // Outbound
          6,6,7,8,9,11,12,13,14,15,16,16,16,17,17,17,17,17,18,18,18,18,19,20,21,22,23,0,1,
        ];
        allScheduleTimes.forEach((h) => {
          if (hours[h]) hours[h].metra++;
        });

        // Freight windows
        const freightHours = [
          [1,2,3], [10,11], [14,15], [20,21], // UP
          [2,3,4], [9,10], [13,14], [18,19],  // IHB
        ];
        freightHours.forEach((window) => {
          window.forEach((h) => {
            if (hours[h]) hours[h].freight++;
          });
        });

        const maxTotal = Math.max(...hours.map((h) => h.metra + h.freight), 1);
        const currentHour = new Date().getHours();

        // Peak periods
        const peaks = [
          { time: '6:00 – 8:30 AM', label: 'Morning Rush', type: 'Metra + Freight', severity: 'high' },
          { time: '4:00 – 6:30 PM', label: 'Evening Rush', type: 'Metra heavy outbound', severity: 'high' },
          { time: '10:00 – 11:30 AM', label: 'Mid-Morning', type: 'Freight window', severity: 'medium' },
          { time: '2:00 – 3:30 PM', label: 'Afternoon', type: 'Freight window', severity: 'medium' },
          { time: '1:00 – 4:00 AM', label: 'Overnight', type: 'Freight corridor', severity: 'low' },
        ];

        return (
          <>
            <button
              onClick={() => setShowPatterns(!showPatterns)}
              style={styles.patternBar}
            >
              <div style={styles.patternBarInner}>
                <div style={styles.patternBarLeft}>
                  <i className="ri-bar-chart-2-line" style={{ fontSize: 14, color: GRAY_400 }}></i>
                  <span style={styles.patternBarText}>Train Patterns</span>
                  <span style={styles.patternBarStat}>~100 trains/day</span>
                </div>
                <i className={showPatterns ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}
                  style={{ fontSize: 18, color: GRAY_400 }}></i>
              </div>
            </button>

            {showPatterns && (
              <section style={styles.patternPanel}>
                {/* Hourly density chart */}
                <div style={styles.patternChart}>
                  <div style={styles.patternChartLabel}>
                    <span>HOURLY TRAIN DENSITY</span>
                    <span style={styles.patternLegend}>
                      <span style={styles.legendDot('#6B8AFF')}></span> Metra
                      <span style={styles.legendDot(ORANGE)}></span> Freight
                    </span>
                  </div>
                  <div style={styles.patternBars}>
                    {hours.map((h) => {
                      const metraHeight = (h.metra / maxTotal) * 48;
                      const freightHeight = (h.freight / maxTotal) * 48;
                      const isCurrent = h.hour === currentHour;
                      const isRush = (h.hour >= 6 && h.hour <= 8) || (h.hour >= 16 && h.hour <= 18);
                      return (
                        <div key={h.hour} style={styles.patternBarCol}>
                          <div style={{ ...styles.patternBarStack, height: 48 }}>
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: '15%',
                              right: '15%',
                              height: metraHeight,
                              background: isCurrent ? WHITE : '#6B8AFF',
                              opacity: isCurrent ? 1 : 0.7,
                            }} />
                            <div style={{
                              position: 'absolute',
                              bottom: metraHeight,
                              left: '15%',
                              right: '15%',
                              height: freightHeight,
                              background: isCurrent ? ORANGE : ORANGE,
                              opacity: isCurrent ? 1 : 0.5,
                            }} />
                          </div>
                          <span style={{
                            ...styles.patternBarHour,
                            color: isCurrent ? WHITE : isRush ? GRAY_400 : GRAY_600,
                            fontWeight: isCurrent ? 800 : 500,
                          }}>
                            {h.hour % 3 === 0 ? h.label : ''}
                          </span>
                          {isCurrent && <div style={styles.patternBarNow} />}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Peak times */}
                <div style={styles.patternPeaks}>
                  <p style={styles.patternPeaksTitle}>WHEN TO AVOID CROSSINGS</p>
                  {peaks.map((p, i) => (
                    <div key={i} style={styles.patternPeakRow}>
                      <div style={{
                        ...styles.patternPeakDot,
                        background: p.severity === 'high' ? ORANGE : p.severity === 'medium' ? '#FFAA00' : GRAY_600,
                      }} />
                      <div style={{ flex: 1 }}>
                        <div style={styles.patternPeakTime}>{p.time}</div>
                        <div style={styles.patternPeakLabel}>{p.label} — {p.type}</div>
                      </div>
                      <span style={{
                        ...styles.patternPeakSeverity,
                        color: p.severity === 'high' ? ORANGE : p.severity === 'medium' ? '#FFAA00' : GRAY_600,
                      }}>
                        {p.severity === 'high' ? 'HEAVY' : p.severity === 'medium' ? 'MODERATE' : 'LOW'}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Quick stats */}
                <div style={styles.patternStats}>
                  <div style={styles.patternStatItem}>
                    <span style={styles.patternStatNum}>61</span>
                    <span style={styles.patternStatLabel}>Day Trains</span>
                  </div>
                  <div style={styles.patternStatDivider} />
                  <div style={styles.patternStatItem}>
                    <span style={styles.patternStatNum}>40</span>
                    <span style={styles.patternStatLabel}>Night Trains</span>
                  </div>
                  <div style={styles.patternStatDivider} />
                  <div style={styles.patternStatItem}>
                    <span style={styles.patternStatNum}>59</span>
                    <span style={styles.patternStatLabel}>Metra/Day</span>
                  </div>
                  <div style={styles.patternStatDivider} />
                  <div style={styles.patternStatItem}>
                    <span style={styles.patternStatNum}>70</span>
                    <span style={styles.patternStatLabel}>Max MPH</span>
                  </div>
                </div>

                <p style={styles.patternSource}>Source: FRA Crossing Inventory · Metra UP-W Published Schedule · Estimated freight patterns</p>
                <p style={{...styles.patternSource, marginTop: 4, fontStyle: 'italic'}}>Metra times are scheduled — delays not reflected. Freight windows are estimates only. Real-time data comes from community reports.</p>
              </section>
            )}
          </>
        );
      })()}

      {/* ─── ACTIVE ALERTS ─── */}
      {hasActiveBlockage && (
        <section style={styles.section}>
          <div style={styles.sectionInner}>
            <div style={styles.sectionHeader}>
              <div style={styles.activeDot} />
              <h3 style={styles.sectionTitle}>Active Now</h3>
            </div>
            {activeAlerts.map((alert, i) => (
              <div key={alert.id} style={{
                ...styles.alertCard,
                borderBottom: '1px solid #333',
              }}>
                <div style={styles.alertTop}>
                  <div>
                    <p style={styles.alertCrossing}>{alert.crossing}</p>
                    <p style={styles.alertMeta}>
                      {alert.source} · {formatTime(alert.timestamp)}
                    </p>
                  </div>
                  <div style={styles.alertRight}>
                    {alert.eta && (
                      <div style={styles.etaBadge}>
                        <span style={styles.etaNum}>~{alert.eta}</span>
                        <span style={styles.etaUnit}>min</span>
                      </div>
                    )}
                    <i className="ri-arrow-right-line" style={{ fontSize: 20, color: '#FF4D00' }}></i>
                  </div>
                </div>
                <p style={styles.alertConfirm}>
                  {alert.confirmations} confirmed
                </p>
              </div>
            ))}
            {crowdBlockedCrossings
              .filter((c) => !activeAlerts.some((a) => a.crossing === c.name))
              .map((crossing) => {
                const cs = crossingStatuses[crossing.id] || {};
                const trainVotes = cs.confirmations?.train_present || 0;
                return (
                  <div key={crossing.id} style={{
                    ...styles.alertCard,
                    borderBottom: '1px solid #333',
                  }}>
                    <div style={styles.alertTop}>
                      <div>
                        <p style={styles.alertCrossing}>{crossing.name}</p>
                        <p style={styles.alertMeta}>
                          Community Report · {crossing.cross}
                        </p>
                      </div>
                      <div style={styles.alertRight}>
                        <i className="ri-arrow-right-line" style={{ fontSize: 20, color: '#FF4D00' }}></i>
                      </div>
                    </div>
                    <p style={styles.alertConfirm}>
                      {trainVotes} confirmed
                    </p>
                  </div>
                );
              })}
          </div>
        </section>
      )}

      {/* ─── FREIGHT WARNINGS ─── */}
      {schedule.freight.length > 0 && (
        <section style={styles.freightSection}>
          <div style={styles.freightHeader}>
            <i className="ri-error-warning-fill" style={{ fontSize: 14, color: ORANGE }}></i>
            <span style={styles.freightHeaderText}>ESTIMATED FREIGHT WINDOW</span>
          </div>
          {schedule.freight.map((f, i) => (
            <div key={`f-${i}`} style={styles.freightCard}>
              <div style={styles.freightTop}>
                <div>
                  <span style={styles.freightCarrier}>{f.carrier}</span>
                  <span style={styles.freightStatus}>
                    {f.active ? 'WINDOW ACTIVE' : `EST. ~${f.minutesUntil} MIN`}
                  </span>
                </div>
                <span style={styles.freightWindow}>{f.startTime} – {f.endTime}</span>
              </div>
              <p style={styles.freightLabel}>{f.label}</p>
              <div style={styles.freightCrossings}>
                {f.crossings.map((c) => (
                  <span key={c} style={styles.freightCrossingTag}>
                    <i className="ri-roadster-line" style={{ fontSize: 10 }}></i> {c}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* ─── METRA TIMELINE ─── */}
      {schedule.metra.length > 0 && (
        <section style={styles.metraStrip}>
          <div style={styles.metraStripHeader}>
            <span style={styles.metraStripLabel}>METRA UP-W · SCHEDULED</span>
            <span style={styles.metraStripDay}>{schedule.dayType.toUpperCase()}</span>
          </div>
          <div style={styles.metraStripRow}>
            {schedule.metra.filter(t => t.minutesUntil >= 0).slice(0, 8).map((t, i) => {
              const isImminent = t.minutesUntil <= 3;
              return (
                <div key={`m-${i}`} style={styles.metraStripItem}>
                  <span style={{
                    ...styles.metraStripTime,
                    ...(isImminent ? { color: ORANGE } : {}),
                  }}>
                    {t.time.replace(/ (AM|PM)/, '')}
                  </span>
                  <span style={styles.metraStripAmPm}>
                    {t.time.includes('AM') ? 'AM' : 'PM'}
                  </span>
                  <span style={{
                    ...styles.metraStripDir,
                    ...(isImminent ? { color: ORANGE } : {}),
                  }}>
                    {t.minutesUntil <= 1 ? 'NOW' : `${t.minutesUntil}m`}
                  </span>
                  <span style={styles.metraStripArrow}>
                    {t.direction === 'Inbound' ? (
                      <i className="ri-arrow-left-line" style={{ fontSize: 10 }}></i>
                    ) : (
                      <i className="ri-arrow-right-line" style={{ fontSize: 10 }}></i>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ─── NAV TABS ─── */}
      <div style={styles.navBar}>
        <div style={styles.navInner}>
          {['crossings', 'reports'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveView(tab)}
              style={{
                ...styles.navTab,
                ...(activeView === tab ? styles.navTabActive : {}),
              }}
            >
              {tab === 'crossings' ? 'Crossings' : `Reports (${nearbyReports.length})`}
            </button>
          ))}
        </div>
      </div>

      {/* ─── CROSSINGS VIEW ─── */}
      {activeView === 'crossings' && (
        <section style={styles.section}>
          <div style={styles.sectionInner}>
            {TRAIN_CROSSINGS.map((crossing, i) => {
              const isActive = activeAlerts.some((a) => a.crossing === crossing.name);
              const alert = activeAlerts.find((a) => a.crossing === crossing.name);
              const cs = crossingStatuses[crossing.id] || {};
              const crowdStatus = cs.current_status || 'unknown';
              const isBlocked = isActive || crowdStatus === 'train_present'; // Only human-confirmed
              const isSlowTraffic = !isBlocked && crowdStatus === 'slow_traffic'; // Traffic probe only
              const trafficRatio = cs.traffic_ratio || null;
              const trainCount = cs.confirmations?.train_present || 0;
              const clearCount = cs.confirmations?.clear || 0;
              return (
                <div
                  key={crossing.id}
                  style={{
                    ...styles.crossingCard,
                    ...(isBlocked ? styles.crossingCardActive : {}),
                    ...(isSlowTraffic ? styles.crossingCardSlow : {}),
                    ...(i < TRAIN_CROSSINGS.length - 1 ? { borderBottom: (isBlocked || isSlowTraffic) ? 'none' : '1px solid #222' } : {}),
                  }}
                >
                  <div style={styles.crossingTop}>
                    {/* Swiss-style crossroad icon */}
                    <div style={{
                      ...styles.crossIcon,
                      borderColor: isBlocked ? ORANGE : isSlowTraffic ? '#FFAA00' : '#333',
                    }}>
                      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                        <rect x="14" y="0" width="8" height="36" fill={isBlocked ? ORANGE : isSlowTraffic ? '#FFAA00' : '#444'} />
                        <rect x="0" y="14" width="36" height="8" fill={isBlocked ? ORANGE : isSlowTraffic ? '#FFAA00' : '#333'} />
                        <rect x="2" y="12" width="2" height="12" rx="0.5" fill={isBlocked ? '#cc3d00' : isSlowTraffic ? '#cc8800' : '#555'} />
                        <rect x="8" y="12" width="2" height="12" rx="0.5" fill={isBlocked ? '#cc3d00' : isSlowTraffic ? '#cc8800' : '#555'} />
                        <rect x="26" y="12" width="2" height="12" rx="0.5" fill={isBlocked ? '#cc3d00' : isSlowTraffic ? '#cc8800' : '#555'} />
                        <rect x="32" y="12" width="2" height="12" rx="0.5" fill={isBlocked ? '#cc3d00' : isSlowTraffic ? '#cc8800' : '#555'} />
                        <circle cx="18" cy="18" r="3" fill={isBlocked ? WHITE : isSlowTraffic ? '#FFAA00' : '#666'} />
                      </svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={styles.crossingLabelRow}>
                        <span style={styles.carrierBadge}>{crossing.carrier}</span>
                        <span style={styles.subdivBadge}>{crossing.subdivision}</span>
                        {isBlocked && <span style={styles.statusLive}>BLOCKED</span>}
                        {isSlowTraffic && <span style={styles.statusSlow}>SLOW TRAFFIC</span>}
                        {!isBlocked && !isSlowTraffic && crowdStatus === 'clear' && <span style={styles.statusClear}>CLEAR</span>}
                        {!isBlocked && !isSlowTraffic && crowdStatus === 'unknown' && <span style={styles.statusUnknown}>—</span>}
                      </div>
                      <h4 style={{
                        ...styles.crossingName,
                        color: isBlocked ? ORANGE : isSlowTraffic ? '#FFAA00' : WHITE,
                      }}>
                        {crossing.name}
                      </h4>
                      <p style={styles.crossingCross}>{crossing.cross}</p>
                      {isBlocked && alert?.eta && (
                        <p style={styles.crossingEta}>Est. {alert.eta} min delay</p>
                      )}
                      {isBlocked && crossing.typical_duration && (
                        <p style={styles.crossingDuration}>Typical: {crossing.typical_duration}</p>
                      )}
                      {isSlowTraffic && (
                        <p style={styles.crossingSlowNote}>
                          Traffic {trafficRatio ? `${trafficRatio}x slower` : 'slower'} than usual — could be a train, red light, or congestion. Tap "Train" if you see one.
                        </p>
                      )}
                    </div>
                    <i className="ri-arrow-right-up-line" style={{ fontSize: 24, color: isBlocked ? ORANGE : isSlowTraffic ? '#FFAA00' : '#555', flexShrink: 0 }}></i>
                  </div>
                  {/* Train On / Clear quick-tap buttons */}
                  <div style={styles.statusBtns}>
                    <button
                      onClick={() => updateCrossingStatus(crossing.id, 'train_present')}
                      style={{
                        ...styles.statusBtn,
                        ...(crowdStatus === 'train_present' ? styles.statusBtnTrainActive : {}),
                      }}
                    >
                      <i className="ri-train-line" style={{ fontSize: 14 }}></i>
                      <span>Train</span>
                      {trainCount > 0 && <span style={styles.statusBtnCount}>{trainCount}</span>}
                    </button>
                    <button
                      onClick={() => updateCrossingStatus(crossing.id, 'clear')}
                      style={{
                        ...styles.statusBtn,
                        ...(crowdStatus === 'clear' ? styles.statusBtnClearActive : {}),
                      }}
                    >
                      <i className="ri-check-line" style={{ fontSize: 14 }}></i>
                      <span>Clear</span>
                      {clearCount > 0 && <span style={styles.statusBtnCount}>{clearCount}</span>}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ─── REPORTS VIEW ─── */}
      {activeView === 'reports' && (
        <section style={styles.section}>
          <div style={styles.sectionInner}>
            {nearbyReports.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={styles.emptyTitle}>No Reports Yet</p>
                <p style={styles.emptySub}>Be the first to report a train.</p>
              </div>
            ) : (
              nearbyReports.map((report, i) => (
                <div
                  key={report.id}
                  style={{
                    ...styles.reportCard,
                    ...(i < nearbyReports.length - 1 ? { borderBottom: '1px solid #222' } : {}),
                  }}
                >
                  <div style={styles.reportHeader}>
                    <div>
                      <p style={styles.reportCrossing}>{report.crossing}</p>
                      <p style={styles.reportMeta}>
                        {report.reportedBy} · {formatTime(report.timestamp)}
                      </p>
                    </div>
                    <i className="ri-arrow-right-line" style={{ fontSize: 16, color: '#555' }}></i>
                  </div>
                  <p style={styles.reportText}>{report.text}</p>
                  <button
                    onClick={() => handleVote(report.id)}
                    style={{
                      ...styles.voteBtn,
                      ...(userVoted[report.id] ? styles.voteBtnActive : {}),
                    }}
                  >
                    <i className="ri-arrow-up-s-line" style={{ fontSize: 14 }}></i>
                    {report.votes}
                  </button>
                </div>
              ))
            )}
          </div>
        </section>
      )}

      {/* ─── SPACER FOR FAB ─── */}
      <div style={{ height: 120 }} />

      {/* ─── REPORT FAB ─── */}
      <button
        onClick={() => {
          requestNotifications();
          setShowReportModal(true);
        }}
        style={styles.fab}
      >
        <span style={styles.fabText}>Report a Train!</span>
        <i className="ri-arrow-right-up-line" style={{ fontSize: 20, color: '#000' }}></i>
      </button>

      {/* ─── REPORT MODAL ─── */}
      {showReportModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Report a Train</h3>
              <button onClick={() => setShowReportModal(false)} style={styles.modalClose}>
                <i className="ri-close-line" style={{ fontSize: 20 }}></i>
              </button>
            </div>

            <div style={styles.modalDivider} />

            <div style={styles.modalBody}>
              <div style={styles.fieldGroup}>
                <label style={styles.fieldLabel}>Crossing</label>
                <select
                  value={selectedCrossing}
                  onChange={(e) => setSelectedCrossing(e.target.value)}
                  style={styles.select}
                >
                  <option value="">Select a crossing...</option>
                  {TRAIN_CROSSINGS.map((c) => (
                    <option key={c.id} value={c.name}>{c.name} &amp; {c.cross}</option>
                  ))}
                  <option value="Other">Other location</option>
                </select>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.fieldLabel}>What did you see?</label>
                <textarea
                  value={reportText}
                  onChange={(e) => setReportText(e.target.value)}
                  placeholder="Train crossing now... heard horn from west..."
                  style={styles.textarea}
                  rows="3"
                />
              </div>

              <div style={styles.locationBox}>
                <p style={styles.locationLabel}>Location</p>
                {userLocation ? (
                  <p style={styles.locationCoords}>
                    {userLocation.lat.toFixed(4)}, {userLocation.lng.toFixed(4)}
                  </p>
                ) : (
                  <p style={styles.locationCoords}>Getting location...</p>
                )}
              </div>

              <button
                onClick={handleReportTrain}
                disabled={!reportText.trim()}
                style={{
                  ...styles.submitBtn,
                  opacity: reportText.trim() ? 1 : 0.3,
                  cursor: reportText.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                <i className="ri-send-plane-fill" style={{ fontSize: 16 }}></i>
                <span>Submit Report</span>
                <i className="ri-arrow-right-line" style={{ fontSize: 16 }}></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── BOTTOM BAR ─── */}
      <div style={styles.bottomBar}>
        <div style={styles.bottomInner}>
          <div style={styles.statBlock}>
            <span style={styles.statNum}>{totalBlockedCount}</span>
            <span style={styles.statLabel}>Active</span>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.statBlock}>
            <span style={styles.statNum}>{nearbyReports.length}</span>
            <span style={styles.statLabel}>Reports</span>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.statBlock}>
            <span style={styles.statNum}>{RADIUS_MILES}</span>
            <span style={styles.statLabel}>Miles</span>
          </div>
        </div>
      </div>

      {/* ─── WELCOME MODAL ─── */}
      {showWelcome && (
        <div style={styles.modalOverlay}>
          <div style={{...styles.modal, maxWidth: 400}}>
            <div style={{ padding: '28px 24px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <div style={{
                  width: 48, height: 48, background: ORANGE, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <i className="ri-arrow-right-up-line" style={{ fontSize: 28, color: BLACK }}></i>
                </div>
                <div>
                  <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: WHITE, letterSpacing: '-0.03em' }}>WTF?</h2>
                  <p style={{ fontSize: 12, color: GRAY_400, margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Where's The Freight?</p>
                </div>
              </div>

              <p style={{ fontSize: 15, color: GRAY_100, lineHeight: 1.5, margin: '0 0 20px', fontWeight: 500 }}>
                Real-time crossing alerts for Elmhurst, IL — powered by neighbors like you.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={styles.welcomeIconWrap}>
                    <i className="ri-group-line" style={{ fontSize: 16, color: ORANGE }}></i>
                  </div>
                  <div>
                    <p style={styles.welcomeItemTitle}>Community-Powered</p>
                    <p style={styles.welcomeItemDesc}>Tap "Train" or "Clear" on any crossing to report what you see. More reports = better accuracy for everyone.</p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={styles.welcomeIconWrap}>
                    <i className="ri-line-chart-line" style={{ fontSize: 16, color: ORANGE }}></i>
                  </div>
                  <div>
                    <p style={styles.welcomeItemTitle}>Best Available Data</p>
                    <p style={styles.welcomeItemDesc}>We combine Metra schedules, traffic flow analysis, FRA crossing data, and community reports. No single source is perfect.</p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={styles.welcomeIconWrap}>
                    <i className="ri-error-warning-line" style={{ fontSize: 16, color: ORANGE }}></i>
                  </div>
                  <div>
                    <p style={styles.welcomeItemTitle}>Estimates, Not Guarantees</p>
                    <p style={styles.welcomeItemDesc}>Freight railroads do not publish schedules. Freight windows are estimated (±60-120 min). Metra times are scheduled, not real-time. Crossing status auto-expires after 30 minutes.</p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={styles.welcomeIconWrap}>
                    <i className="ri-notification-3-line" style={{ fontSize: 16, color: ORANGE }}></i>
                  </div>
                  <div>
                    <p style={styles.welcomeItemTitle}>Get Alerts</p>
                    <p style={styles.welcomeItemDesc}>Enable notifications to get pinged when crossings near you are reported blocked. Add to your home screen for the best experience.</p>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ padding: '0 24px 24px' }}>
              <button
                onClick={() => {
                  setShowWelcome(false);
                  try { localStorage.setItem('wtf_welcomed', '1'); } catch {}
                }}
                style={{
                  width: '100%', padding: '14px 20px', background: ORANGE,
                  border: 'none', color: BLACK, fontSize: 15, fontWeight: 800,
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 8, letterSpacing: '-0.01em',
                }}
              >
                Got It — Show Me Crossings
                <i className="ri-arrow-right-line" style={{ fontSize: 18 }}></i>
              </button>
              <p style={{
                fontSize: 10, color: GRAY_600, textAlign: 'center', marginTop: 12,
                lineHeight: 1.4, fontWeight: 500,
              }}>
                Not affiliated with Union Pacific, Metra, or the Village of Elmhurst.
                <br />Use at your own risk. Not for safety-critical decisions.
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

/* ═══════════════════════════════════════════
   STYLES — Swiss / International Typographic
   ═══════════════════════════════════════════ */

const styles = {
  // ─── APP ───
  app: {
    minHeight: '100vh',
    background: BLACK,
    color: WHITE,
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    WebkitFontSmoothing: 'antialiased',
  },

  // ─── HEADER ───
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 40,
    background: BLACK,
    borderBottom: `1px solid ${GRAY_800}`,
  },
  headerInner: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  logoMark: {
    width: 36,
    height: 36,
    background: WHITE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoArrow: {
    fontSize: 20,
    color: BLACK,
    lineHeight: 1,
  },
  logoText: {
    fontSize: 20,
    fontWeight: 900,
    letterSpacing: '-0.02em',
    lineHeight: 1,
    color: WHITE,
    margin: 0,
  },
  logoSub: {
    fontSize: 9,
    fontWeight: 500,
    color: WHITE,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    margin: 0,
    marginTop: 1,
    opacity: 0.6,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerLocation: {
    fontSize: 11,
    color: WHITE,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    opacity: 0.6,
  },
  refreshBtn: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    color: WHITE,
    border: `1px solid ${WHITE}`,
    borderRadius: '50%',
    cursor: 'pointer',
    opacity: 0.8,
    transition: 'opacity 0.15s',
    padding: 0,
    fontFamily: 'inherit',
  },
  menuBtn: {
    padding: '6px 14px',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    background: 'transparent',
    color: WHITE,
    border: `1px solid ${WHITE}`,
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontFamily: 'inherit',
  },
  menuPanel: {
    background: GRAY_900,
    borderTop: `1px solid ${GRAY_800}`,
  },
  menuInner: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '8px 20px',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    padding: '14px 0',
    background: 'none',
    border: 'none',
    color: WHITE,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'none',
    fontFamily: 'inherit',
  },
  menuDivider: {
    height: 1,
    background: GRAY_800,
  },
  menuBadge: {
    marginLeft: 'auto',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: GREEN,
  },
  menuAbout: {
    fontSize: 12,
    color: GRAY_400,
    lineHeight: 1.6,
    margin: '14px 0 0',
  },
  menuLegal: {
    fontSize: 10,
    color: GRAY_600,
    lineHeight: 1.6,
    margin: '12px 0 0',
  },
  installCard: {
    background: GRAY_800,
    borderRadius: 8,
    padding: '14px 16px',
    margin: '4px 0',
  },
  installHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  installTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: WHITE,
    letterSpacing: '-0.01em',
  },
  installText: {
    fontSize: 12,
    color: GRAY_400,
    lineHeight: 1.6,
    margin: 0,
  },

  // ─── HERO ───
  hero: {
    marginTop: 61,
    background: ORANGE,
  },
  heroInner: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '32px 20px 24px',
  },
  heroTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  heroLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: BLACK,
    margin: 0,
  },
  heroTime: {
    fontSize: 11,
    fontWeight: 500,
    color: BLACK,
    margin: 0,
    fontVariantNumeric: 'tabular-nums',
  },
  heroTitle: {
    fontSize: 56,
    fontWeight: 900,
    lineHeight: 0.95,
    letterSpacing: '-0.03em',
    color: BLACK,
    margin: 0,
    marginBottom: 20,
  },
  heroDivider: {
    height: 2,
    background: 'rgba(0,0,0,0.3)',
    marginBottom: 12,
  },
  heroMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: BLACK,
  },

  // ─── TRAIN PATTERNS ───
  patternBar: {
    display: 'block',
    width: '100%',
    background: GRAY_900,
    border: 'none',
    borderBottom: `1px solid ${GRAY_800}`,
    padding: '10px 0',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  patternBarInner: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '0 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  patternBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  patternBarText: {
    fontSize: 11,
    fontWeight: 700,
    color: WHITE,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  patternBarStat: {
    fontSize: 10,
    fontWeight: 600,
    color: GRAY_600,
    letterSpacing: '0.04em',
  },
  patternPanel: {
    background: GRAY_900,
    borderBottom: `1px solid ${GRAY_800}`,
    padding: '0 0 16px',
  },
  patternChart: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '0 20px 16px',
  },
  patternChartLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: '0.1em',
    color: GRAY_600,
    marginBottom: 10,
    textTransform: 'uppercase',
  },
  patternLegend: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 8,
    fontWeight: 600,
    letterSpacing: '0.06em',
  },
  legendDot: (color) => ({
    display: 'inline-block',
    width: 6,
    height: 6,
    background: color,
    marginLeft: 8,
  }),
  patternBars: {
    display: 'flex',
    gap: 0,
    height: 62,
    alignItems: 'flex-end',
  },
  patternBarCol: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
  },
  patternBarStack: {
    width: '100%',
    position: 'relative',
  },
  patternBarHour: {
    fontSize: 8,
    color: GRAY_600,
    marginTop: 4,
    fontVariantNumeric: 'tabular-nums',
  },
  patternBarNow: {
    position: 'absolute',
    top: -4,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 4,
    height: 4,
    background: WHITE,
    borderRadius: '50%',
  },
  patternPeaks: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '0 20px 12px',
    borderTop: `1px solid ${GRAY_800}`,
    paddingTop: 14,
  },
  patternPeaksTitle: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: '0.1em',
    color: GRAY_600,
    marginBottom: 10,
    margin: '0 0 10px',
  },
  patternPeakRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '7px 0',
    borderBottom: `1px solid #1a1a1a`,
  },
  patternPeakDot: {
    width: 6,
    height: 6,
    flexShrink: 0,
  },
  patternPeakTime: {
    fontSize: 13,
    fontWeight: 800,
    color: WHITE,
    letterSpacing: '-0.01em',
    fontVariantNumeric: 'tabular-nums',
  },
  patternPeakLabel: {
    fontSize: 10,
    color: GRAY_400,
    marginTop: 1,
  },
  patternPeakSeverity: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: '0.08em',
    flexShrink: 0,
  },
  patternStats: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '12px 20px 0',
    display: 'flex',
    borderTop: `1px solid ${GRAY_800}`,
    paddingTop: 14,
  },
  patternStatItem: {
    flex: 1,
    textAlign: 'center',
  },
  patternStatNum: {
    display: 'block',
    fontSize: 22,
    fontWeight: 900,
    color: WHITE,
    letterSpacing: '-0.02em',
    lineHeight: 1,
  },
  patternStatLabel: {
    display: 'block',
    fontSize: 8,
    fontWeight: 700,
    color: GRAY_600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginTop: 4,
  },
  patternStatDivider: {
    width: 1,
    background: GRAY_800,
    alignSelf: 'stretch',
  },
  patternSource: {
    maxWidth: 640,
    margin: '12px auto 0',
    padding: '0 20px',
    fontSize: 8,
    color: GRAY_600,
    fontStyle: 'italic',
    letterSpacing: '0.02em',
  },

  // ─── FREIGHT WARNINGS ───
  freightSection: {
    background: '#0D0800',
    borderBottom: `1px solid ${GRAY_800}`,
    padding: '16px 20px',
  },
  freightHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  freightHeaderText: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.12em',
    color: ORANGE,
  },
  freightCard: {
    background: 'rgba(255,77,0,0.06)',
    border: `1px solid rgba(255,77,0,0.3)`,
    padding: '14px 16px',
    marginBottom: 8,
  },
  freightTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  freightCarrier: {
    fontSize: 13,
    fontWeight: 900,
    color: WHITE,
    marginRight: 10,
  },
  freightStatus: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.08em',
    color: ORANGE,
    background: 'rgba(255,77,0,0.15)',
    padding: '3px 8px',
  },
  freightWindow: {
    fontSize: 11,
    fontWeight: 600,
    color: GRAY_400,
    whiteSpace: 'nowrap',
  },
  freightLabel: {
    fontSize: 12,
    color: GRAY_400,
    margin: 0,
    marginBottom: 10,
  },
  freightCrossings: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  freightCrossingTag: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.04em',
    background: 'rgba(255,77,0,0.1)',
    color: ORANGE,
    padding: '3px 8px',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    textTransform: 'uppercase',
  },

  // ─── METRA STRIP ───
  metraStrip: {
    background: GRAY_900,
    borderBottom: `1px solid ${GRAY_800}`,
    padding: '10px 20px',
  },
  metraStripHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  metraStripLabel: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: '0.12em',
    color: GRAY_600,
  },
  metraStripDay: {
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: '0.08em',
    color: GRAY_600,
  },
  metraStripRow: {
    display: 'flex',
    gap: 0,
    overflowX: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  metraStripItem: {
    flex: '1 0 auto',
    textAlign: 'center',
    padding: '4px 10px',
    borderRight: `1px solid ${GRAY_800}`,
    minWidth: 0,
  },
  metraStripTime: {
    fontSize: 13,
    fontWeight: 800,
    color: GRAY_400,
    letterSpacing: '-0.02em',
    fontVariantNumeric: 'tabular-nums',
  },
  metraStripAmPm: {
    fontSize: 8,
    fontWeight: 700,
    color: GRAY_600,
    marginLeft: 1,
  },
  metraStripDir: {
    display: 'block',
    fontSize: 9,
    fontWeight: 700,
    color: GRAY_600,
    marginTop: 1,
  },
  metraStripArrow: {
    display: 'block',
    fontSize: 10,
    color: GRAY_600,
    marginTop: 1,
  },

  // ─── SECTIONS ───
  section: {
    borderBottom: `1px solid ${GRAY_800}`,
  },
  sectionInner: {
    maxWidth: 640,
    margin: '0 auto',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '16px 20px 0',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: GRAY_400,
    margin: 0,
  },
  activeDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: ORANGE,
    animation: 'pulse 2s infinite',
  },

  // ─── ALERT CARDS ───
  alertCard: {
    padding: '16px 20px',
  },
  alertTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  alertCrossing: {
    fontSize: 18,
    fontWeight: 800,
    color: ORANGE,
    margin: 0,
    letterSpacing: '-0.01em',
  },
  alertMeta: {
    fontSize: 11,
    color: GRAY_600,
    margin: 0,
    marginTop: 2,
  },
  alertRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  etaBadge: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 3,
  },
  etaNum: {
    fontSize: 28,
    fontWeight: 900,
    color: ORANGE,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },
  etaUnit: {
    fontSize: 11,
    fontWeight: 600,
    color: GRAY_600,
    textTransform: 'uppercase',
  },
  alertConfirm: {
    fontSize: 11,
    color: GRAY_600,
    margin: 0,
    marginTop: 8,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },

  // ─── NAV ───
  navBar: {
    borderBottom: `1px solid ${GRAY_800}`,
    background: BLACK,
    position: 'sticky',
    top: 61,
    zIndex: 30,
  },
  navInner: {
    maxWidth: 640,
    margin: '0 auto',
    display: 'flex',
  },
  navTab: {
    flex: 1,
    padding: '14px 20px',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: GRAY_600,
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.15s',
    textAlign: 'center',
  },
  navTabActive: {
    color: WHITE,
    borderBottomColor: ORANGE,
  },

  // ─── CROSSING CARDS ───
  crossingCard: {
    padding: '20px 20px',
    transition: 'background 0.15s',
  },
  crossingCardActive: {
    background: GRAY_900,
    borderLeft: `3px solid ${ORANGE}`,
  },
  crossingCardSlow: {
    background: '#12110A',
    borderLeft: '3px solid #FFAA00',
  },
  crossingTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  },
  crossIcon: {
    width: 52,
    height: 52,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #333',
    flexShrink: 0,
  },
  crossingLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  carrierBadge: {
    fontSize: 10,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: GRAY_400,
    padding: '2px 6px',
    border: `1px solid ${GRAY_800}`,
  },
  statusLive: {
    fontSize: 10,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: ORANGE,
  },
  statusClear: {
    fontSize: 10,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: '#4ADE80',
  },
  crossingName: {
    fontSize: 22,
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
    margin: 0,
  },
  crossingCross: {
    fontSize: 11,
    color: GRAY_400,
    margin: 0,
    marginTop: 2,
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  crossingEta: {
    fontSize: 12,
    color: ORANGE,
    margin: 0,
    marginTop: 4,
    fontWeight: 600,
  },
  crossingDuration: {
    fontSize: 10,
    color: GRAY_600,
    margin: 0,
    marginTop: 2,
    fontWeight: 500,
    fontStyle: 'italic',
  },
  crossingSlowNote: {
    fontSize: 11,
    color: '#FFAA00',
    margin: 0,
    marginTop: 6,
    fontWeight: 500,
    lineHeight: 1.4,
    opacity: 0.85,
  },
  subdivBadge: {
    fontSize: 9,
    fontWeight: 600,
    color: GRAY_600,
    letterSpacing: '0.04em',
  },
  statusSlow: {
    fontSize: 10,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: '#FFAA00',
  },
  statusUnknown: {
    fontSize: 10,
    fontWeight: 800,
    color: GRAY_600,
  },

  // ─── STATUS BUTTONS ───
  statusBtns: {
    display: 'flex',
    gap: 8,
    marginTop: 12,
    paddingTop: 12,
    borderTop: `1px solid #222`,
  },
  statusBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '10px 0',
    background: 'transparent',
    border: `1px solid #333`,
    color: GRAY_400,
    fontSize: 12,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    position: 'relative',
    transition: 'all 0.15s',
  },
  statusBtnTrainActive: {
    background: 'rgba(255,77,0,0.12)',
    borderColor: ORANGE,
    color: ORANGE,
  },
  statusBtnClearActive: {
    background: 'rgba(42,157,92,0.12)',
    borderColor: GREEN,
    color: GREEN,
  },
  statusBtnCount: {
    fontSize: 10,
    fontWeight: 800,
    background: GRAY_800,
    padding: '1px 6px',
    marginLeft: 2,
  },

  // ─── WELCOME MODAL ───
  welcomeIconWrap: {
    width: 32,
    height: 32,
    background: 'rgba(255,77,0,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 2,
  },
  welcomeItemTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: WHITE,
    margin: '0 0 3px',
    letterSpacing: '-0.01em',
  },
  welcomeItemDesc: {
    fontSize: 12,
    color: GRAY_400,
    margin: 0,
    lineHeight: 1.5,
    fontWeight: 500,
  },

  // ─── REPORT CARDS ───
  reportCard: {
    padding: '20px 20px',
  },
  reportHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  reportCrossing: {
    fontSize: 15,
    fontWeight: 800,
    color: WHITE,
    margin: 0,
    letterSpacing: '-0.01em',
  },
  reportMeta: {
    fontSize: 11,
    color: GRAY_600,
    margin: 0,
    marginTop: 2,
  },
  reportText: {
    fontSize: 14,
    color: GRAY_100,
    margin: 0,
    marginBottom: 12,
    lineHeight: 1.5,
  },
  voteBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 12px',
    fontSize: 12,
    fontWeight: 700,
    color: GRAY_600,
    background: 'transparent',
    border: `1px solid ${GRAY_800}`,
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontVariantNumeric: 'tabular-nums',
  },
  voteBtnActive: {
    color: ORANGE,
    borderColor: ORANGE,
  },

  // ─── EMPTY STATE ───
  emptyState: {
    padding: '64px 20px',
    textAlign: 'center',
  },
  emptyTitle: {
    fontSize: 24,
    fontWeight: 800,
    color: GRAY_600,
    margin: 0,
    marginBottom: 8,
  },
  emptySub: {
    fontSize: 14,
    color: GRAY_600,
    margin: 0,
  },

  // ─── FAB ───
  fab: {
    position: 'fixed',
    bottom: 80,
    right: 20,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '14px 24px',
    background: ORANGE,
    color: BLACK,
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    zIndex: 20,
    transition: 'transform 0.15s',
    boxShadow: `0 4px 24px ${ORANGE}40`,
  },
  fabText: {
    lineHeight: 1,
  },

  // ─── MODAL ───
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.85)',
    backdropFilter: 'blur(4px)',
    zIndex: 50,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    padding: 16,
  },
  modal: {
    background: GRAY_900,
    border: `1px solid ${GRAY_800}`,
    width: '100%',
    maxWidth: 480,
    maxHeight: '85vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 20px 16px',
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 900,
    letterSpacing: '-0.02em',
    margin: 0,
    color: WHITE,
  },
  modalClose: {
    background: 'transparent',
    border: 'none',
    color: GRAY_600,
    cursor: 'pointer',
    padding: 4,
  },
  modalDivider: {
    height: 1,
    background: GRAY_800,
  },
  modalBody: {
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  fieldLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: GRAY_400,
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    fontWeight: 500,
    background: BLACK,
    color: WHITE,
    border: `1px solid ${GRAY_800}`,
    outline: 'none',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    appearance: 'none',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    fontWeight: 500,
    background: BLACK,
    color: WHITE,
    border: `1px solid ${GRAY_800}`,
    outline: 'none',
    resize: 'none',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    lineHeight: 1.5,
  },
  locationBox: {
    padding: '10px 12px',
    background: BLACK,
    border: `1px solid ${GRAY_800}`,
  },
  locationLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: GRAY_400,
    margin: 0,
    marginBottom: 2,
  },
  locationCoords: {
    fontSize: 12,
    color: GRAY_600,
    margin: 0,
    fontVariantNumeric: 'tabular-nums',
  },
  submitBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 20px',
    fontSize: 13,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    background: ORANGE,
    color: BLACK,
    border: 'none',
    cursor: 'pointer',
    transition: 'opacity 0.15s',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },

  // ─── BOTTOM BAR ───
  bottomBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: BLACK,
    borderTop: `1px solid ${GRAY_800}`,
    zIndex: 30,
  },
  bottomInner: {
    maxWidth: 640,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    padding: '12px 20px',
  },
  statBlock: {
    flex: 1,
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: 6,
  },
  statNum: {
    fontSize: 24,
    fontWeight: 900,
    color: WHITE,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },
  statLabel: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: GRAY_600,
  },
  statDivider: {
    width: 1,
    height: 24,
    background: GRAY_800,
  },

};

export default WTFApp;
